import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, ChevronDown, Wrench, Briefcase, CalendarDays, Puzzle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { trackSkills } from '@/lib/skillsAnalytics';

const OPTIONS = [
  { to: '/submit', icon: Wrench, title: 'Submit a Tool', description: 'Add an AI tool to the directory.' },
  { to: '/skills/submit', icon: Puzzle, title: 'Submit a Skill', description: 'Share a reusable AI skill.' },
  { to: '/post-job', icon: Briefcase, title: 'Post a Job', description: 'Reach AI professionals.' },
  { to: '/events/submit', icon: CalendarDays, title: 'Submit an Event', description: 'Share an AI event.' },
];

interface SubmitMenuProps {
  className?: string;
  variant?: 'outline' | 'default';
  hover?: boolean;
  onSelect?: () => void;
}

export function SubmitMenu({ className, variant = 'outline', hover = true, onSelect }: SubmitMenuProps) {
  const [open, setOpen] = useState(false);
  const closeTimer = useRef<number | null>(null);

  const canHover = () => hover && window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  const enter = () => {
    if (!canHover()) return;
    if (closeTimer.current) window.clearTimeout(closeTimer.current);
    setOpen(true);
  };
  const leave = () => {
    if (!canHover()) return;
    if (closeTimer.current) window.clearTimeout(closeTimer.current);
    closeTimer.current = window.setTimeout(() => setOpen(false), 150);
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen} modal={false}>
      <DropdownMenuTrigger asChild>
        <Button
          variant={variant}
          size="sm"
          className={cn('gap-1.5', className)}
          onMouseEnter={enter}
          onMouseLeave={leave}
        >
          <Plus className="h-4 w-4" />
          Submit
          <ChevronDown className={cn('h-3.5 w-3.5 opacity-70 transition-transform', open && 'rotate-180')} />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        sideOffset={6}
        collisionPadding={12}
        className="w-72 rounded-xl p-1.5 shadow-lg"
        onMouseEnter={enter}
        onMouseLeave={leave}
      >
        {OPTIONS.map(({ to, icon: Icon, title, description }) => (
          <DropdownMenuItem key={to} asChild className="cursor-pointer rounded-lg p-2.5">
            <Link to={to} onClick={() => { if (to === '/skills/submit') trackSkills('skill_submit_click'); setOpen(false); onSelect?.(); }} className="flex items-start gap-3">
              <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                <Icon className="h-4 w-4" />
              </span>
              <span className="flex flex-col">
                <span className="text-sm font-medium">{title}</span>
                <span className="text-xs text-muted-foreground">{description}</span>
              </span>
            </Link>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
